'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/context/LanguageContext';
import styles from './AnnouncementBar.module.scss';

const ROTATE_INTERVAL = 4000;

// Free shipping – delivery truck
const truckIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 3h15v13H1z" />
    <path d="M16 8h4l3 3v5h-7z" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

// Generic payment – credit card (used for non-DKK currencies)
const cardIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </svg>
);

// Satisfaction / money-back – two arrows forming a circle
const cycleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

export default function AnnouncementBar() {
  const { t, currency } = useTranslation();
  const items: string[] = t.announcement?.items ?? [];

  const renderIcon = (i: number) => {
    if (i === 1) {
      return currency === 'dkk' ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src="/images/mobilepay-mark.png" alt="" className={styles.logoImg} />
      ) : (
        cardIcon
      );
    }
    if (i === 2) return cycleIcon;
    return truckIcon;
  };
  const [index, setIndex] = useState(0);
  const pausedRef = useRef(false);

  const count = items.length;

  useEffect(() => {
    if (count <= 1) return;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      setIndex((prev) => (prev + 1) % count);
    }, ROTATE_INTERVAL);
    return () => clearInterval(id);
  }, [count]);

  if (count === 0) return null;

  const safeIndex = index % count;

  const goTo = (next: number) => {
    setIndex(((next % count) + count) % count);
  };

  return (
    <div
      className={styles.bar}
      role="region"
      aria-label={t.announcement?.label ?? 'Tilbud'}
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
    >
      <div className={styles.inner}>
        <button
          type="button"
          className={styles.arrow}
          onClick={() => goTo(safeIndex - 1)}
          aria-label={t.announcement?.prev ?? 'Forrige'}
        >
          <svg viewBox="0 0 10 6" width="11" height="7" fill="none" aria-hidden="true">
            <path d="M9 5L5 1L1 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" transform="rotate(-90 5 3)" />
          </svg>
        </button>

        <div className={styles.viewport}>
          <ul
            className={styles.track}
            style={{ transform: `translateX(-${safeIndex * 100}%)` }}
            aria-live="polite"
          >
            {items.map((text, i) => (
              <li
                key={i}
                className={styles.slide}
                aria-hidden={i !== safeIndex}
              >
                <span className={styles.icon}>{renderIcon(i)}</span>
                <span className={styles.text}>{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <button
          type="button"
          className={styles.arrow}
          onClick={() => goTo(safeIndex + 1)}
          aria-label={t.announcement?.next ?? 'Næste'}
        >
          <svg viewBox="0 0 10 6" width="11" height="7" fill="none" aria-hidden="true">
            <path d="M9 5L5 1L1 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" transform="rotate(90 5 3)" />
          </svg>
        </button>
      </div>
    </div>
  );
}
